import {
  FeaturedMostSearchedProceduresQueryDocument,
  FeaturedMostSearchedProceduresQueryQuery,
  MostSearchedProceduresQueryDocument,
  MostSearchedProceduresQueryQuery,
} from '@medprev-web-app/api-graphql-client';

import { SearchResult } from '@/app/(main)/_components/search-modal/search-modal.types';
import { ApolloClient } from '@/graphql/apollo-client';
import { MostSearchedResultType, SearchType } from '@/types/most-search-type';
import { mostSearchedGroupLabels } from '@/services/most-searched/most-searched-group-labels.const';

type MostSearchedGroupResponse = MostSearchedProceduresQueryQuery['mostSearchedProcedures'][number];
type FeaturedProcedureResponse = FeaturedMostSearchedProceduresQueryQuery['featuredMostSearchedProcedures'][number];

let featuredProcedures: SearchResult[] | undefined;
let featuredProceduresRequest: Promise<SearchResult[]> | undefined;

function toMostSearchedResult(group: MostSearchedGroupResponse): MostSearchedResultType {
  const type = String(group.type) as SearchType;

  return {
    ...mostSearchedGroupLabels[type],
    type,
    procedures: group.procedures.map((procedure) => ({
      id: String(procedure.id),
      code: procedure.code,
      name: procedure.name,
      displayName: procedure.displayName,
      slug: procedure.slug,
      alias: procedure.alias,
      searchTerm: procedure.searchTerm,
      type: String(procedure.type) as SearchType,
    })),
  };
}

export async function getMostSearchedGroups(): Promise<MostSearchedResultType[]> {
  const { data } = await ApolloClient.query({
    query: MostSearchedProceduresQueryDocument,
  });

  return (data?.mostSearchedProcedures ?? []).map(toMostSearchedResult);
}

export async function getMostSearchedProcedures(): Promise<MostSearchedResultType[]> {
  const groups = await getMostSearchedGroups();

  return groups.filter((group) => group.type !== SearchType.VACCINE);
}

function toFeaturedSearchResult(procedure: FeaturedProcedureResponse): SearchResult {
  return {
    id: String(procedure.id),
    code: procedure.code,
    displayName: procedure.displayName,
    slug: procedure.slug,
    searchType: String(procedure.type) as SearchType,
  };
}

async function fetchFeaturedMostSearchedProcedures(): Promise<SearchResult[]> {
  const { data } = await ApolloClient.query({
    query: FeaturedMostSearchedProceduresQueryDocument,
  });

  return (data?.featuredMostSearchedProcedures ?? []).map(toFeaturedSearchResult);
}

export function getLoadedFeaturedMostSearchedProcedures(): SearchResult[] | undefined {
  return featuredProcedures;
}

export function getFeaturedMostSearchedProcedures(): Promise<SearchResult[]> {
  if (featuredProcedures) {
    return Promise.resolve(featuredProcedures);
  }

  featuredProceduresRequest ??= fetchFeaturedMostSearchedProcedures()
    .then((procedures) => {
      if (procedures.length > 0) {
        featuredProcedures = procedures;
      }

      return procedures;
    })
    .finally(() => {
      featuredProceduresRequest = undefined;
    });

  return featuredProceduresRequest;
}
